import { useState } from "react";
import { DeviceMobile, List, LockKey, SignOut, X } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import ChangePassword from "@/components/ChangePassword";
import ChangeLoginMobile from "@/components/ChangeLoginMobile";
import NotificationBell from "@/components/NotificationBell";
import FloatingAskEdujac from "@/components/FloatingAskEdujac";
import LanguageSelector from "./LanguageSelector";
import { useTranslation } from "react-i18next";
export default function DashboardShell({ brand, nav, active, onNavigate, children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showChangePw, setShowChangePw] = useState(false);
  const [showLoginMobile, setShowLoginMobile] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const doLogout = () => {
    logout();
    navigate("/login");
  };
  return (
    <div className="min-h-screen bg-[#F4F5F7]">
      <aside className="hidden md:flex fixed inset-y-0 left-0 w-60 min-h-0 flex-col bg-white border-r border-slate-200 z-20">
        <div className="flex items-center gap-2 px-5 py-5">
          <img src="/edujac-mark.png" alt="EDUJAC" className="w-9 h-9 object-contain" />
          <div>
            <p className="font-heading font-extrabold leading-none tracking-wide text-[#00194E]">EDUJAC</p>
            <p className="text-xs text-slate-500 mt-0.5">{brand}</p>
          </div>
        </div>
        <nav className="flex-1 min-h-0 overflow-y-auto px-3 space-y-1 mt-2 pb-3">
          {nav.map((item) => (
            <button
              key={item.id}
              data-testid={`nav-${item.id}`}
              onClick={() => onNavigate(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors duration-200 ${
                active === item.id
                  ? "bg-brand-coral/10 text-brand-coralDark"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
            ><item.icon size={18} weight={active === item.id ? "duotone" : "regular"} />
              {item.labelKey ? t(item.labelKey) : item.label}
            </button>
          ))}
        </nav>
      <div className="px-4 py-3 border-t border-slate-100">
        <LanguageSelector placement="up" />
      </div>
        <div className="p-4 border-t border-slate-100 flex items-center justify-between">
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate" data-testid="user-name">{user?.name}</p>
            <p className="text-xs text-slate-500 capitalize">{user?.role?.replace("_", " ")}</p>
          </div>
          <NotificationBell onNavigate={onNavigate} placement="up" />
          <button
            data-testid="change-login-mobile-btn"
            onClick={() => setShowLoginMobile(true)}
            className="p-2 rounded-lg text-slate-400 hover:text-brand-coral hover:bg-brand-coral/10 transition-colors duration-200"
            title="My Account · Login mobile"
          >
            <DeviceMobile size={18} />
          </button>
          <button
            data-testid="change-password-btn"
            onClick={() => setShowChangePw(true)}
            className="p-2 rounded-lg text-slate-400 hover:text-brand-coral hover:bg-brand-coral/10 transition-colors duration-200"
            title={t("account.changePassword")}
          >
            <LockKey size={18} />
          </button>
          <button
            data-testid="logout-btn"
            onClick={doLogout}
            className="p-2 rounded-lg text-slate-400 hover:text-brand-coral hover:bg-brand-coral/10 transition-colors duration-200"
            title={t("account.logout")}
          >
            <SignOut size={18} />
          </button>
        </div>
      </aside>

      <div className="md:pl-60">
        <header className="md:hidden sticky top-0 z-20 backdrop-blur-xl bg-white/85 border-b border-white/40 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/edujac-mark.png" alt="EDUJAC" className="w-7 h-7 object-contain" />
            <p className="font-heading font-bold text-sm">{brand}</p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <div className="md:hidden">
              <LanguageSelector />
            </div>
            <NotificationBell onNavigate={onNavigate} />
            <button
              type="button"
              onClick={() => setMobileMenuOpen((current) => !current)}
              className="p-2 text-slate-500 hover:text-brand-coral"
              aria-label={t("common.menu", { defaultValue: "Menu" })}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X size={20} /> : <List size={20} />}
            </button>
          </div>
        </header>
        {mobileMenuOpen && (
          <div className="md:hidden fixed inset-x-0 top-[57px] z-30 bg-white border-b border-slate-200 shadow-lg max-h-[calc(100vh-57px)] overflow-y-auto">
            <nav className="p-3 space-y-1">
              {nav.map((item) => (
                <button
                  key={item.id}
                  data-testid={`nav-mobile-${item.id}`}
                  onClick={() => {
                    onNavigate(item.id);
                    setMobileMenuOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-left ${
                    active === item.id
                      ? "bg-brand-coral/10 text-brand-coralDark"
                      : "text-slate-600 hover:bg-slate-100"
                  }`}
                ><item.icon size={18} />
                  <span>{item.labelKey ? t(item.labelKey) : item.label}</span>
                </button>
              ))}
            </nav>
            <div className="border-t border-slate-100 p-3">
              <div className="rounded-xl bg-slate-50 px-3 py-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold">{user?.name}</p>
                  <p className="text-xs capitalize text-slate-500">{user?.role?.replace("_", " ")}</p>
                </div>
              </div>
              <div className="mt-2 grid grid-cols-3 gap-2">
                <button
                  data-testid="change-login-mobile-btn-mobile"
                  onClick={() => { setShowLoginMobile(true); setMobileMenuOpen(false); }}
                  className="flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-2 py-2.5 text-xs font-semibold text-slate-600"
                >
                  <DeviceMobile size={16} /> Mobile
                </button>
                <button
                  data-testid="change-password-btn-mobile"
                  onClick={() => { setShowChangePw(true); setMobileMenuOpen(false); }}
                  className="flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-3 py-2.5 text-xs font-semibold text-slate-600"
                >
                  <LockKey size={16} /> {t("account.changePassword")}
                </button>
                <button
                  data-testid="logout-btn-mobile"
                  onClick={doLogout}
                  className="flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-3 py-2.5 text-xs font-semibold text-slate-600"
                >
                  <SignOut size={16} /> {t("account.logout")}
                </button>
              </div>
            </div>
          </div>
        )}
        <main className="min-w-0 overflow-x-hidden p-4 md:p-8 max-w-6xl mx-auto">{children}</main>
      </div>
      <ChangePassword open={showChangePw} onClose={() => setShowChangePw(false)} />
      <ChangeLoginMobile open={showLoginMobile} onClose={() => setShowLoginMobile(false)} />
      <FloatingAskEdujac role={user?.role} onNavigate={onNavigate} />
    </div>
  );
}
